<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'workSummary',
    path: '/worksheets-work-summary',
    component: 'WorkSummaryView',
    title: () => pgettext('WorkSummaryView', 'Work summary'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'WorkSummaryView',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const selectedItems = ref([]);
  const params = ref({
    date_from: $fn.value.today(),
    date_to: null,
  });
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_headerShowReloadButton = computed(() => false);
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'service_name',
      name: $fn.value.pgettext('WorkSummaryView', 'Service name'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'product',
        navigateOnIconClick: true,
        params: {
          id: 'service_id',
        },
      },
    },
    {
      field: 'unit',
      name: $fn.value.pgettext('WorkSummaryView', 'unit'),
    },
    {
      field: 'quantity',
      name: $fn.value.pgettext('WorkSummaryView', 'Quantity'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'net_amount',
      name: $fn.value.pgettext('WorkSummaryView', 'Net'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'vat_amount',
      name: $fn.value.pgettext('WorkSummaryView', 'VAT'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'amount',
      name: $fn.value.pgettext('WorkSummaryView', 'Amount'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'rental_name',
      name: $fn.value.pgettext('WorkSummaryView', 'Rental'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'rental',
        navigateOnIconClick: true,
        params: {
          id: 'rental_id',
        },
      },
    },
    {
      field: 'company_name',
      name: $fn.value.pgettext('WorkSummaryView', 'Company'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'company_id',
        },
      },
    },
    {
      field: 'client_name',
      width: '200px',
      name: $fn.value.pgettext('WorkSummaryView', 'Company'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'client_id',
        },
      },
    },
    {
      field: 'contractor_name',
      width: '200px',
      name: $fn.value.pgettext('WorkSummaryView', 'Contractor'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'contractor_id',
        },
      },
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    params: params.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_headerShowReloadButton:
      b_2248226175642056_headerShowReloadButton.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = () => {
    return $fn.value.gridDataSource('query_worksheets_work_summary', {
      parameters: () => params.value,
      assignUniqueId: true,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      {
        name: 'params',
        cache: { type: 'local', key: 'WorkSummaryViewFilter' },
      },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      {
        name: 'b_2248226175642056_headerShowReloadButton',
        nodeId: '2248226175642056',
      },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedItems,
      params,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_headerShowReloadButton,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('WorkSummaryView', 'Work summary'),
      icon: $config.value.worksheet.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_6rhos6o4s_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_0y9bsd12d_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_showModal_5mcewemsp_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        modal: true,
        oid: $event.data.data.id,
      },
    };
  };
  const inst_evh_2248226175642056_command_iipfiofnz_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'transaction_number';
  };
  const act_4615685607825208_setData_37q5o0mwv_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'params.date_from',
      value: $event.data[0],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4615685607825208_setData_p4cdqnis8_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'params.date_to',
      value: $event.data[1],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_7172922356531116_script_cqm9rqexy_value_callable = async (
    $event
  ) => {
    return $refs.grid.reload();
  };
  const act_7172922356531116_script_cqm9rqexy_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7172922356531116_script_cqm9rqexy_value_callable($event);
      },
    };
  };

  const act_7315092382398562_closeModal_6rhos6o4s =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_6rhos6o4s_getActionArgs,
    });
  const inst_evh_7315092382398562_close_whtuzi93a =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_6rhos6o4s,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_whtuzi93a],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_xkkow86hu =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_owzimp8h1 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_xkkow86hu,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_owzimp8h1],
    event: 'reload',
  });
  const act_2248226175642056_setData_0y9bsd12d = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_0y9bsd12d_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_e8ocftgwu =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_0y9bsd12d,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_e8ocftgwu],
      event: 'selectedRowsChanged',
    });
  const act_2248226175642056_showModal_5mcewemsp =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_5mcewemsp_getActionArgs,
    });
  const inst_evh_2248226175642056_command_iipfiofnz =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_5mcewemsp,
      event: 'command',
      when: inst_evh_2248226175642056_command_iipfiofnz_getWhen,
      displayName: 'showModal',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_command_iipfiofnz],
    event: 'command',
  });
  const act_4615685607825208_setData_37q5o0mwv = new actionTypes.SetDataAction({
    actionArgs: act_4615685607825208_setData_37q5o0mwv_getActionArgs,
  });
  const inst_evh_4615685607825208_changed_q1n2lkrx9 =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_setData_37q5o0mwv,
      event: 'changed',
      displayName: 'setData',
    });
  const act_4615685607825208_setData_p4cdqnis8 = new actionTypes.SetDataAction({
    actionArgs: act_4615685607825208_setData_p4cdqnis8_getActionArgs,
  });
  const inst_evh_4615685607825208_changed_jc0w89dow =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_setData_p4cdqnis8,
      event: 'changed',
      displayName: 'setData',
    });
  const inst_evh_4615685607825208_changed = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_4615685607825208_changed_q1n2lkrx9,
      inst_evh_4615685607825208_changed_jc0w89dow,
    ],
    event: 'changed',
  });
  const act_7172922356531116_script_cqm9rqexy = new actionTypes.ScriptAction({
    actionArgs: act_7172922356531116_script_cqm9rqexy_getActionArgs,
  });
  const inst_evh_7172922356531116_clickPreventStop_2dc0d95wl =
    new actionTypes.EventHandlerImpl({
      action: act_7172922356531116_script_cqm9rqexy,
      event: 'click.prevent.stop',
      displayName: 'script',
    });
  const inst_evh_7172922356531116_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7172922356531116_clickPreventStop_2dc0d95wl],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_whtuzi93a,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_owzimp8h1,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_e8ocftgwu,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2248226175642056_command_iipfiofnz,
    inst_evh_2248226175642056_command,
    inst_evh_4615685607825208_changed_q1n2lkrx9,
    inst_evh_4615685607825208_changed_jc0w89dow,
    inst_evh_4615685607825208_changed,
    inst_evh_7172922356531116_clickPreventStop_2dc0d95wl,
    inst_evh_7172922356531116_clickPreventStop,
  };

  const evh_7315092382398562_close_whtuzi93a = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_whtuzi93a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_owzimp8h1 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_owzimp8h1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_e8ocftgwu = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_e8ocftgwu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_iipfiofnz = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_iipfiofnz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_changed_q1n2lkrx9 = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_changed_q1n2lkrx9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_changed_jc0w89dow = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_changed_jc0w89dow.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_changed = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_changed.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7172922356531116_clickPreventStop_2dc0d95wl = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7172922356531116_clickPreventStop_2dc0d95wl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7172922356531116_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7172922356531116_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="WorkSummaryView"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpDataGrid
      name="workSummary"
      :headerTitle="pgettext('WorkSummaryView', 'Work summary')"
      :items="ds"
      checkbox-row-selection
      footer-totals
      show-column-picker
      :headerShowReloadButton="b_2248226175642056_headerShowReloadButton"
      export-to-csv
      :exportToCsvFilename="pgettext('ExportFilename', 'work-summary')"
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      v-if="loaded"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
      @command="evh_2248226175642056_command($event, {})"
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2 align-items-center">
          <DateRangeSelector
            name="paymentFilters"
            :from="params.date_from"
            :to="params.date_to"
            @changed="evh_4615685607825208_changed($event, {})"
          >
          </DateRangeSelector>
          <div>
            <ItpButton
              variant="light"
              :icon="$config.reloadIcon"
              :title="pgettext('WorkSummaryViewFilters', 'Load data')"
              :spinning="actions && actions.load && actions.load.isRunning"
              @click.prevent.stop="
                evh_7172922356531116_clickPreventStop($event, {})
              "
            >
            </ItpButton>
          </div>
        </div>
      </template>
    </ItpDataGrid>
  </ItpPage>
</template>
